import { Card, Spinner, cn } from "@playerelite/ui";
import { Direction } from "./analytics-utils";
import { ChartTone } from "./TimeSeriesChart";

type Props = {
  label: string;
  /** Undefined while loading. */
  value?: string;
  /** Formatted change against the previous period; null when there is no previous period. */
  change: string | null;
  direction: Direction;
  /** "30 days", completing "vs previous 30 days". */
  periodLabel: string;
  trend: number[];
  tone: ChartTone;
  isError: boolean;
};

const SPARK_W = 96;
const SPARK_H = 32;

const sparkPath = (values: number[]) => {
  if (values.length < 2) return "";
  const hi = Math.max(...values);
  const lo = Math.min(...values);
  const span = hi - lo || 1;
  return values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * SPARK_W;
      const y = SPARK_H - 2 - ((v - lo) / span) * (SPARK_H - 4);
      return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
};

const KpiCard = ({
  label,
  value,
  change,
  direction,
  periodLabel,
  trend,
  tone,
  isError,
}: Props) => (
  <Card padding="md" className="flex min-w-0 flex-col gap-2">
    <span className="font-mono text-xs font-medium uppercase tracking-wider text-fg-muted">
      {label}
    </span>
    <div className="flex min-h-[2.25rem] items-end justify-between gap-3">
      {value !== undefined ? (
        <span className="font-mono text-2xl font-semibold tabular-nums text-fg">{value}</span>
      ) : isError ? (
        <span className="text-sm text-fg-muted">Couldn't load</span>
      ) : (
        <Spinner size="sm" label={`Loading ${label.toLowerCase()}`} className="text-fg-muted" />
      )}
      {value !== undefined && trend.length > 1 ? (
        <svg
          width={SPARK_W}
          height={SPARK_H}
          viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
          className="shrink-0"
          aria-hidden="true"
        >
          <path
            d={sparkPath(trend)}
            fill="none"
            strokeWidth={2}
            strokeLinejoin="round"
            className={tone === "primary" ? "stroke-action" : "stroke-warning"}
          />
        </svg>
      ) : null}
    </div>
    {value !== undefined ? (
      <span className="text-sm text-fg-muted">
        {change ? (
          <>
            <span
              className={cn(
                "font-semibold",
                direction === "up" && "text-success-subtle-fg",
                direction === "down" && "text-danger-subtle-fg",
                direction === "flat" && "text-fg"
              )}
            >
              {change}
            </span>{" "}
            vs previous {periodLabel}
          </>
        ) : (
          "No earlier period to compare"
        )}
      </span>
    ) : null}
  </Card>
);

export default KpiCard;
